import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    forwardRef,
    Injector,
    Input,
    OnChanges,
    OnInit
} from '@angular/core';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {TypedSimpleChanges} from '../../../../common/typed-on-changes';
import {AccountService} from '../../../../components/accounts';
import {AutocompleteProviderConfig, DefinedListAutocompleteService} from '../../../../components/autocomplete';
import {AutocompleteInputComponent} from '../../../../components/autocomplete-input';
import {DisplayExchangeAccountFactory} from '../../../../factories/display-exchange-account.factory';
import {ExchangeAccount, ExchangeAccountBase, isExchangeAccountBaseEqual} from '../../../../models/exchange-account';

export interface AccountAutocompleteListItem {
    account: ExchangeAccount;
    displayName: string;
}

@Component({
    selector: 'account-autocomplete',
    templateUrl: '../../../../components/autocomplete-input/autocomplete-input.component.html',
    styleUrls: ['../../../../components/autocomplete-input/autocomplete-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AccountAutocompleteComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AccountAutocompleteComponent<OuterValue = ExchangeAccount,
    InnerValue extends AccountAutocompleteListItem = AccountAutocompleteListItem>
    extends AutocompleteInputComponent<OuterValue, InnerValue>
    implements OnInit, OnChanges {

    @Input()
    public accountFilter: (account: ExchangeAccount) => boolean = () => true;

    @Input()
    public isEmptyAllowed: boolean = false;

    constructor(
        elementRef: ElementRef,
        injector: Injector,
        protected accountService: AccountService
    ) {
        super(elementRef, injector);
    }

    public override ngOnInit(): void {
        this.updateAutocompleteConfig();

        super.ngOnInit();
    }

    public override ngOnChanges(changes: TypedSimpleChanges<AccountAutocompleteComponent<OuterValue, InnerValue>>): void {
        super.ngOnChanges(changes);

        const updateNeeded = changes.accountFilter && !changes.accountFilter.firstChange
            && changes.isEmptyAllowed && !changes.isEmptyAllowed.firstChange;

        if (updateNeeded) {
            this.updateAutocompleteConfig();
        }
    }

    protected override outerValueToInnerValue(outerValue: OuterValue): InnerValue {
        return outerValue
            ? this.createListItem(outerValue as unknown as ExchangeAccount) as InnerValue
            : null;
    }

    protected override innerValueToOuterValue(innerValue: InnerValue): OuterValue {
        return innerValue
            ? innerValue.account as unknown as OuterValue
            : null;
    }

    protected override isOuterValuesEqual(outerValue1: OuterValue, outerValue2: OuterValue): boolean {
        const accountBase1: ExchangeAccountBase = outerValue1 as unknown as ExchangeAccountBase;
        const accountBase2: ExchangeAccountBase = outerValue2 as unknown as ExchangeAccountBase;

        return isExchangeAccountBaseEqual(accountBase1, accountBase2);
    }

    /**
     * Separating this method allows to generate a custom list in successor class.
     * Example: {@link AccountBaseAutocompleteComponent}
     */
    protected getAvailableList(): InnerValue[] {
        return this.accountService.getAllAccountsFromCache()
            .map<InnerValue>(account => this.createListItem(account));
    }

    protected createListItem(account: ExchangeAccount): InnerValue {
        return {
            account,
            displayName: DisplayExchangeAccountFactory.getDisplayAccountName(account)
        } as InnerValue;
    }

    private updateAutocompleteConfig(): void {
        const ACCOUNT_SEARCH_FIELD: keyof AccountAutocompleteListItem = 'displayName';

        const list: AccountAutocompleteListItem[] = this.getAvailableList()
            .filter(item => !this.accountFilter || this.accountFilter(item.account))
            .sort((item1, item2) =>
                item1.displayName.localeCompare(item2.displayName, undefined, {numeric: true})
            );

        const autocompleteProvider = new DefinedListAutocompleteService<AccountAutocompleteListItem>({
            list,
            filterByField: ACCOUNT_SEARCH_FIELD
        });

        if (!this.autocompleteConfig) {
            this.autocompleteConfig = {
                type:                      {
                                               provider: autocompleteProvider
                                           } as AutocompleteProviderConfig<AccountAutocompleteListItem>,
                displayValue:              (item: AccountAutocompleteListItem) => item && item.displayName || '',
                allowVariantsForEmptyTerm: true,
                disallowEmptySelect:       !this.isEmptyAllowed,
                allowVariantOrNull:        !!this.isEmptyAllowed,
                selectAllOnFocus:          true
            };
        } else {
            (this.autocompleteConfig.type as AutocompleteProviderConfig<AccountAutocompleteListItem>)
                .provider = autocompleteProvider;
        }
    }

}
